import { useEffect, useState } from "react";

import { Box, Button, Divider, FormControl, FormErrorMessage, FormLabel, Heading, Input, Progress, Switch, useDisclosure, useToast } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { useNavigate } from "react-router-dom"
import { HttpStatusCode } from "axios";

import { COLORS, DURATION_TOAST } from "@/common/constants";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import Loader from "@/components/Layouts/Loader";
import SelectUserTypeRefData from "@/components/Selects/SelectUserTypeRefData";
import { changePasswordSchema } from "@/schemas/changePasswordSchema";
import { changePasswordUser, getUserProfile } from "@/services/inspectionUserService";
import { getDataRef } from "@/services/refDataService";

import './styles/Form.css'

const Profile = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const [dataEdit, setDataEdit] = useState(null) as any;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [strength, setStrength] = useState('');
    const [dataDetail, setDataDetail] = useState(null) as any;
    const [defaultCompany, setDefaultCompany] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [listUserType, setListUserType] = useState([]) as any;

    const handleSubmit = async (data: any) => {
        setTitle("Change Password?");
        setConfirmationTest("Are you sure you want to change?");
        setConfirmationTitleButton("Change Password");
        onOpen();
        setDataEdit(data);
        setIsSubmitting(true);
    }

    const generateData = () => {
        const obj = {
            oldPassword: dataEdit.oldPassword.trim(),
            newPassword: dataEdit.newPassword.trim(),
            confirmNewPassword: dataEdit.confirmPassword.trim()
        };
        return obj;
    }

    const handleConfirm = async () => {
        onClose();

        const obj = generateData();
        const response = await changePasswordUser(obj) as any;
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'Password updated.',
                description: "We've updated password for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/settings/profile')
            }, 1000);
        } else {
            toast({
                title: 'Password failed to update.',
                description: "We unabled to update password for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const checkPasswordStrength = (password: string) => {
        let score = 0;

        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[a-z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^a-zA-Z0-9]/.test(password)) score++;

        switch (score) {
            case 0:
                setStrength('');
                break;
            case 1:
            case 2:
                setStrength('Weak');
                break;
            case 3:
                setStrength('Medium');
                break;
            case 4:
                setStrength('Strong');
                break;
            default:
                setStrength('Very Strong');
        }
    };

    const handleChangePassword = (props: any, value: string) => {
        props.setFieldValue('newPassword', value);
        checkPasswordStrength(value);
    };

    const getMyStrengthScore = () => {
        switch (strength) {
            case 'Weak':
                return 25;
            case 'Medium':
                return 50;
            case 'Strong':
                return 75;
            case 'Very Strong':
                return 100;
            default:
                return 0;
        }
    }

    const getMyStrengthColor = () => {
        switch (strength) {
            case 'Weak':
                return 'red';
            case 'Medium':
                return '#3182ce';
            case 'Strong':
                return 'green';
            case 'Very Strong':
                return 'green';
            default:
                return 'transparent';
        }
    }

    const getDataDetail = async () => {
        setIsLoading(true);
        const response = await getUserProfile() as any;
        setIsLoading(false);
        if (response) {
            setDataDetail(response.data);
            setDefaultCompany(response.data.role === 'adminfinance' || response.data.role === 'superadmin' ? 'Pertamina' : '');
        }
    }

    const getDataListType = async () => {
        const response = await getDataRef('usertype') as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                label: item.name,
                value: item.value,
            }));

            setListUserType(formattedData);
        }
    };

    useEffect(() => {
        getDataListType();
        getDataDetail();
    }, []);

    return (
        <>
            {isLoading ? <Loader /> :
                <>
                    <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
                    <div className="form-container">
                        <Formik
                            initialValues={{
                                firstName: dataDetail?.firstName || '',
                                lastName: dataDetail?.lastName || '',
                                email: dataDetail?.userName || '',
                                company: dataDetail?.companyName || defaultCompany,
                                role: dataDetail?.role || '',
                                type: dataDetail?.userType || '',
                                mfa: false,
                                oldPassword: '',
                                newPassword: '',
                                confirmPassword: ''
                            }}
                            validationSchema={changePasswordSchema}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    <Heading as='h4' size='md' marginBottom={4}>
                                        User Profile
                                    </Heading>
                                    <Field name='firstName'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">First Name</FormLabel>
                                                    <Input {...field} placeholder='First Name' className="form-input" disabled />
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='lastName'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Last Name</FormLabel>
                                                    <Input {...field} placeholder='Last Name' className="form-input" disabled />
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='email'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Email</FormLabel>
                                                    <Input {...field} placeholder='Email' className="form-input" disabled />
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='company'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Company</FormLabel>
                                                    <Input {...field} placeholder='Company' className="form-input" disabled />
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='role'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Role</FormLabel>
                                                    <Input {...field} placeholder='Role' className="form-input" disabled />
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='type'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.type && form.touched.type} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <div className="form-input">
                                                            <SelectUserTypeRefData name='type' handleChange={({ value }: any) => props.setFieldValue('type', value)} value={props.values.type} defaultValue={props.values.type} hasError={form.errors.type && form.touched.type} options={listUserType} isDisabled />
                                                        </div>
                                                        <FormErrorMessage>{form.errors.type}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider marginTop={4} marginBottom={4} />
                                    <Field name='mfa'>
                                        {({ field }: any) => (
                                            <FormControl style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Enable 2-Factor Authentication</FormLabel>
                                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
                                                        <Switch {...field} isChecked={props.values.mfa} onChange={() => props.setFieldValue('mfa', !props.values.mfa)} id='status-switch' style={{ marginTop: '5px', backgroundColor: 'white' }} />
                                                        <span style={{ marginTop: '2px', marginLeft: '10px' }}>{props.values.mfa ? 'Active' : 'Inactive'}</span>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Divider marginTop={4} marginBottom={4} />
                                    <Heading as='h4' size='md' marginBottom={4}>
                                        Change Password
                                    </Heading>
                                    <Field name='oldPassword'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.oldPassword && form.touched.oldPassword} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Old Password<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Old Password' type='password' className="form-input" />
                                                        {form.errors.oldPassword && form.touched.oldPassword && (
                                                            <FormErrorMessage>{form.errors.oldPassword}</FormErrorMessage>
                                                        )}
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='newPassword'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.newPassword && form.touched.newPassword} style={{ marginBottom: 15 }}>
                                                <div>
                                                    <div className="form-field-container">
                                                            <FormLabel className="form-label">New Password<span style={{ color: 'red' }}>*</span></FormLabel>
                                                        <div className="form-input-container">
                                                            <Input {...field} placeholder='Input New Password' type='password' className="fom-input" value={props.values.newPassword} onChange={(e) => handleChangePassword(props, e.target.value)} />
                                                            {getMyStrengthScore() > 0 &&
                                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box width='100%' borderRadius='10px' marginTop={4}>
                                                                        <Progress
                                                                            value={getMyStrengthScore()}
                                                                            sx={{
                                                                                backgroundColor: '#e0e0e0',
                                                                                '& > div': {
                                                                                    backgroundColor: getMyStrengthColor(),
                                                                                },
                                                                            }}
                                                                            borderRadius='10px'
                                                                        />
                                                                    </Box>
                                                                </div>
                                                            }
                                                            <div style={{ display: 'flex' }}>
                                                                <span style={{ color: getMyStrengthColor(), width: '100%', textAlign: 'left', fontSize: '0.875rem' }}>{strength}</span>
                                                            </div>
                                                            <FormErrorMessage>{form.errors.newPassword}</FormErrorMessage>
                                                        </div>
                                                        
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='confirmPassword'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.confirmPassword && form.touched.confirmPassword} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel className="form-label">Confirm Password<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Input Confirm Password' type='password' className="form-input" />
                                                        {form.errors.confirmPassword && form.touched.confirmPassword && (
                                                            <FormErrorMessage>{form.errors.confirmPassword}</FormErrorMessage>
                                                        )}
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            bgColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                        >
                                            Change Password
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </>}
        </>
    )
}

export default Profile;